import {getUsersByIds} from '../firebase/firestore/users';
import {collectAuthorsFromPmJson} from './extractAuthorUserIdsFromPmJson';

const AI_AUTHOR = {
    uid: 'ai',
    displayName: 'AI Assistant',
    isAI: true,
};

function isAiAuthor(author) {
    if (!author) return false;
    const uid = typeof author === 'string' ? author : author.uid;
    return uid === 'ai' || author.isAI === true;
}

function normalizeAuthorEntry(entry) {
    if (!entry) return null;
    if (typeof entry === 'string') {
        const uid = entry.trim();
        if (!uid) return null;
        if (uid === 'ai') return {...AI_AUTHOR};
        return {uid, displayName: null, headshot: null};
    }
    if (typeof entry === 'object') {
        // Also handle {value, label} format produced by normalizeMultiSelectValue for author arrays
        const uid = entry.uid ?? entry.id ?? entry.userId ?? entry.value;
        if (!uid) return null;
        if (isAiAuthor({...entry, uid})) return {...AI_AUTHOR, ...entry, uid, isAI: true};
        const rawLabel = entry.displayName || entry.name || entry.label || null;
        // Don't treat the uid itself as a meaningful display name
        const displayName = rawLabel && rawLabel !== uid ? rawLabel : null;
        return {
            uid: String(uid),
            displayName,
            headshot: entry.headshot || entry.photoURL || entry.profilePicture || null,
            profilePicture: entry.profilePicture || null,
            hasAvatar: entry.hasAvatar === true,
            isAI: false,
        };
    }
    return null;
}

function mergeAuthors(existing, incoming) {
    const byUid = new Map();
    for (const author of existing) {
        if (author?.uid) byUid.set(author.uid, author);
    }
    for (const author of incoming) {
        if (!author?.uid) continue;
        const prev = byUid.get(author.uid);
        if (!prev) {
            byUid.set(author.uid, author);
            continue;
        }
        // Prefer the side that has a real display name; never let a UID-as-fallback
        // override a previously resolved human-readable name.
        const displayName = hasRealDisplayName(author)
            ? author.displayName
            : (prev.displayName || author.displayName);
        byUid.set(author.uid, {
            ...prev,
            ...author,
            displayName,
            headshot: author.headshot || prev.headshot,
            profilePicture: author.profilePicture || prev.profilePicture,
            hasAvatar: author.hasAvatar === true || prev.hasAvatar === true,
        });
    }
    return Array.from(byUid.values());
}

function sortAuthors(authors) {
    const human = authors.filter((a) => !isAiAuthor(a));
    const ai = authors.filter((a) => isAiAuthor(a));
    return [...human, ...ai];
}

function isNumericOnlyId(uid) {
    return /^\d+$/.test(String(uid));
}

function hasRealDisplayName(author) {
    const name = (author?.displayName || '').trim();
    const uid = String(author?.uid ?? '');
    return Boolean(name && name !== uid && !isNumericOnlyId(name));
}

/**
 * Drop duplicate author rows (e.g. collaboration client id + resolved DB user for same person).
 */
export function dedupeAuthors(authors) {
    if (!authors?.length) return [];

    const byUid = new Map();
    for (const author of authors) {
        if (!author?.uid) continue;
        const uid = String(author.uid);
        const existing = byUid.get(uid);
        if (!existing) {
            byUid.set(uid, author);
            continue;
        }
        byUid.set(uid, {
            ...existing,
            ...author,
            displayName: hasRealDisplayName(author) ? author.displayName : existing.displayName,
            headshot: author.headshot || existing.headshot,
        });
    }

    let unique = Array.from(byUid.values());
    const named = unique.filter(hasRealDisplayName);

    if (named.length > 0) {
        const seenNames = new Set(named.map((a) => a.displayName.trim().toLowerCase()));
        unique = [
            ...named,
            ...unique.filter((author) => {
                if (hasRealDisplayName(author)) return false;
                if (isNumericOnlyId(author.uid)) return false;
                const name = (author.displayName || '').trim().toLowerCase();
                if (name && seenNames.has(name)) return false;
                return true;
            }),
        ];
    }

    const byName = new Map();
    for (const author of unique) {
        if (hasRealDisplayName(author)) {
            const key = author.displayName.trim().toLowerCase();
            const prev = byName.get(key);
            if (!prev || (isNumericOnlyId(prev.uid) && !isNumericOnlyId(author.uid))) {
                byName.set(key, author);
            }
            continue;
        }
        byName.set(`uid:${author.uid}`, author);
    }

    return Array.from(byName.values());
}

/**
 * Resolve document authors from PM content attribution, version metadata, and report meta.
 * @param {object} params
 * @param {object|null} params.content - ProseMirror doc JSON
 * @param {Array} [params.versionAuthors]
 * @param {Array} [params.metaAuthors]
 * @param {Array} [params.headerAuthors]
 * @param {Array} [params.reportDocAuthors] - flat string[] of user IDs from the report entity (last-resort fallback)
 */
export async function resolveDocumentAuthors({
    content,
    versionAuthors = [],
    metaAuthors = [],
    headerAuthors = [],
    reportDocAuthors = [],
} = {}) {
    const preResolved = [];
    const pendingUids = new Set();

    const ingest = (list) => {
        (list || []).forEach((entry) => {
            const normalized = normalizeAuthorEntry(entry);
            if (!normalized) return;
            if (normalized.displayName || isAiAuthor(normalized)) {
                preResolved.push(normalized);
            } else {
                pendingUids.add(normalized.uid);
            }
        });
    };

    ingest(headerAuthors);
    ingest(metaAuthors);
    ingest(versionAuthors);

    const fromContent = collectAuthorsFromPmJson(content);
    // Fall back to reportDoc authors (flat user-ID strings from the DB) only when no other
    // source supplied any authors — avoids duplicating data that already came from the header.
    if (preResolved.length === 0 && pendingUids.size === 0
        && fromContent.userIds.length === 0 && fromContent.preResolved.length === 0) {
        ingest(reportDocAuthors);
    }
    fromContent.preResolved.forEach((author) => {
        const normalized = normalizeAuthorEntry(author);
        if (!normalized) return;
        if (normalized.displayName || isAiAuthor(normalized)) {
            preResolved.push(normalized);
        } else {
            pendingUids.add(normalized.uid);
        }
    });
    fromContent.userIds.forEach((uid) => pendingUids.add(uid));

    preResolved.forEach((author) => {
        if (isAiAuthor(author)) return;
        const hasAvatarMeta = author.profilePicture || author.hasAvatar || author.headshot;
        if (!hasAvatarMeta) {
            pendingUids.add(author.uid);
        }
    });

    let fetched = [];
    if (pendingUids.size > 0) {
        const uidList = [...pendingUids];
        try {
            const users = await getUsersByIds(uidList);
            const resolvedUids = new Set();
            fetched = (users || []).map((user) => {
                const uid = String(user.userId || user.id || user.uid);
                resolvedUids.add(uid);
                // Skip displayName if it's the raw UID stored as the name (bad DB data).
                const rawDn = user.displayName;
                const displayName = (rawDn && rawDn.trim() !== uid ? rawDn : null)
                    || [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
                    || user.name
                    || user.email
                    || null;
                return {
                    uid,
                    displayName,
                    headshot: user.headshot || user.photoURL || user.profilePicture || null,
                    profilePicture: user.profilePicture || null,
                    hasAvatar: user.hasAvatar === true,
                };
            });
            uidList.forEach((uid) => {
                if (!resolvedUids.has(uid)) {
                    fetched.push({uid, displayName: uid, headshot: null});
                }
            });
        } catch (err) {
            console.warn('[resolveDocumentAuthors] failed to fetch users', err);
            fetched = uidList.map((uid) => ({
                uid,
                displayName: uid,
                headshot: null,
            }));
        }
    }

    const merged = mergeAuthors(preResolved, fetched);
    const normalized = merged.map((author) => {
        if (isAiAuthor(author)) return {...author, displayName: AI_AUTHOR.displayName};
        // Replace UID-as-displayname (unresolvable users) with null so the UI
        // can show "Unknown" rather than a raw UUID/ID string.
        const displayName = hasRealDisplayName(author) ? author.displayName : null;
        return {...author, displayName};
    });
    return sortAuthors(dedupeAuthors(normalized));
}
